'use client';
import cn from 'classnames';
import styles from './AccordionBlock.module.scss';
import { ReactNode, useRef, useState } from 'react';

function AccordionBlockArrow({ isOpen }: { isOpen: boolean }) {
  if (isOpen) {
    return (
      <img src="/svg/minus-button.svg" alt="close" className={styles.icon} />
    );
  }

  return <img src="/svg/plus-button.svg" alt="open" className={styles.icon} />;
}

type AccordionBlockProps = {
  title: string;
  children: ReactNode;
  className?: string;
  content?: ReactNode;
  withPreview?: boolean;
};

export function AccordionBlock({
  title,
  children,
  className,
  content,
  withPreview,
}: AccordionBlockProps) {
  const accordionClassName = cn(styles.accordion, className);
  const [open, setOpen] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(withPreview);
  const ContentRef = useRef<HTMLDivElement | null>(null);

  const getMaxHeight = () => {
    if (!open && !isPreviewMode) return '0px';
    if (isPreviewMode && !open) return '20px';
    if (open && ContentRef.current)
      return ContentRef.current.scrollHeight + 20 + 'px';
    return '0px';
  };

  const [currentMaxHeight, setCurrentMaxHeight] = useState(getMaxHeight());

  const toggleOpening = () => {
    const newOpenState = !open;
    setOpen(newOpenState);

    if (newOpenState) {
      setIsPreviewMode(false);
    } else {
      setIsPreviewMode(withPreview);
    }

    const content = ContentRef.current;
    if (content) {
      if (newOpenState) {
        setCurrentMaxHeight(content.scrollHeight + 20 + 'px');
      } else {
        setCurrentMaxHeight(withPreview ? '20px' : '0px');
      }
    }
  };

  const listClassName = cn(styles.list, {
    [styles.hidden]: !open && !isPreviewMode,
    [styles.preview]: isPreviewMode && !open,
  });

  return (
    <div className={accordionClassName}>
      {content && <div className={styles.content}>{content}</div>}
      <div
        onClick={toggleOpening}
        className={cn(styles.title, {
          [styles.titleActive]: open,
        })}
      >
        {title}
        <div className={styles.icon}>
          <AccordionBlockArrow isOpen={open} />
        </div>
      </div>
      <div
        className={listClassName}
        ref={ContentRef}
        style={{ maxHeight: currentMaxHeight }}
      >
        {children}
      </div>
    </div>
  );
}
